"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion } from "framer-motion";
import { Calendar, CircleCheck as CheckCircle2, ShieldCheck, ArrowRight, Clock, Sparkles, UserCheck, Loader, Mail, Phone } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
const benefits = [{
  id: 1,
  title: "1-on-1 Direct Consultant Access",
  description: "You work directly with me, Yassine Essaadi. No junior account managers or offshore sub-contracting."
}, {
  id: 2,
  title: "Tailored Growth Roadmap",
  description: "No generic automated PDF audits. I manually inspect your website's technical and strategic baseline."
}, {
  id: 3,
  title: "Honest & Transparent Guidance",
  description: "Clear expectations, measurable KPIs, and honest feedback on what it takes to dominate search in your niche."
}];
function ConsultationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="space-y-4 text-center py-8">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-accent/20 text-accent">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">Consultation Requested!</h3>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">
          {successMessage}
        </p>
        <p className="text-xs text-muted-foreground">
          I will review your website details and reply personally within 24 business hours.
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="fullName" rules={{
      required: "Please enter your full name",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Your Full Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Please enter your business email",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="sarah@yourcompany.com" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="websiteUrl" rules={{
      required: "Please enter your website URL",
      minLength: {
        value: 4,
        message: "Please enter a valid URL"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Website URL</FormLabel>
            <FormControl>
              <Input placeholder="https://yourcompany.com" {...field} />
            </FormControl>
            <FormDescription>
              I will analyze your current search presence prior to our call.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="primaryGoal" rules={{
      required: false
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Main SEO Goal or Focus Area (Optional)</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Local visibility, Technical Audit, AI Search" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-md h-12 text-base font-semibold transition-[color,background-color,box-shadow]">
        {isSubmitting ? <>
            <Loader className="animate-spin size-5 mr-2" />
            Analyzing & Scheduling...
          </> : <>
            <SafeHydrate><Calendar className="size-5 mr-2" /></SafeHydrate>
            Book a Free SEO Consultation
          </>}
      </Button>

      <p className="text-center text-xs text-muted-foreground mt-3 flex items-center justify-center gap-1.5">
        <ShieldCheck className="size-4 text-primary" />
        No pressure, no aggressive sales pitch, 100% confidential.
      </p>
    </Form>;
}
export default function Consultation() {
  return <section id="consultationcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border/50 relative overflow-hidden">
      {/* Background Glows */}
      <div className="absolute -top-24 -right-24 size-96 rounded-full bg-secondary/50 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Narrative & Authority */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-muted border border-border text-xs font-medium text-foreground">
                <Sparkles className="size-3.5 text-accent" />
                <span>Direct Freelance SEO Expertise</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight leading-tight">
                Ready for Sustainable <br className="hidden sm:inline" />
                <span className="text-primary">Search Growth?</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
                Book a direct 30-minute strategy session with me. I will audit your business&apos;s digital baseline, evaluate your competitors, and give you clear steps to capture market share.
              </p>
            </div>

            {/* Benefit Bullets */}
            <div className="space-y-4 pt-2">
              {benefits.map((benefit, i) => <div data-index={i} key={benefit.id} className="flex items-start gap-4 p-3.5 rounded-xl bg-card/60 border border-border/80">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <CheckCircle2 className="size-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-card-foreground">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {benefit.description}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Direct Contact Card */}
            <div className="p-5 rounded-2xl bg-muted/50 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_headshot.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_headshot.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_consultant_headshot.png-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="size-12 rounded-full object-cover border-2 border-primary shrink-0" loading="lazy" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    Yassine Essaadi
                    <span className="inline-block size-2 rounded-full bg-emerald-500" title="Available for new clients" />
                  </h4>
                  <p className="text-xs text-muted-foreground">Lead SEO Consultant & Director</p>
                </div>
              </div>

              <div className="flex flex-col sm:items-end gap-1 text-xs text-muted-foreground w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-border">
                <a href="mailto:contact@ye-consultancy.com" className="hover:text-primary transition-colors flex items-center gap-1.5">
                  <Mail className="size-3.5 text-primary" />
                  contact@ye-consultancy.com
                </a>
                <a href="tel:+447490984430" className="hover:text-primary transition-colors flex items-center gap-1.5">
                  <Phone className="size-3.5 text-primary" />
                  +44 7490984430
                </a>
              </div>
            </div>

            {/* Alternative Action */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pt-1">
              <span>Looking for quick answers?</span>
              <Button variant="link" asChild className="p-0 h-auto font-medium text-primary">
                <Link to="/ContactPage" className="inline-flex items-center gap-1">
                  Visit Contact Page
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Lead Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="lg:col-span-5">
            <Card className="shadow-xl border-border bg-card">
              <CardContent className="p-6 sm:p-8">
                <div className="mb-6">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-semibold tracking-wider text-primary uppercase">Direct Booking</span>
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="size-3" /> 30 Min Free Call
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold font-serif text-card-foreground">
                    Get Your SEO Strategy
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fill out the form below and I will get back to you with custom insights and scheduling details.
                  </p>
                </div>

                <FormProvider formId="consultation-cta-form" sectionName="consultationcta" defaultValues={{
                fullName: "",
                email: "",
                websiteUrl: "",
                primaryGoal: ""
              }} validationRules={{
                fullName: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                websiteUrl: {
                  required: true,
                  minLength: 4
                }
              }} submitText="Book a Free SEO Consultation" successMessage="Thank you! Your request has been sent directly to Yassine.">
                  <ConsultationFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}